/**
 * After a fire, does the cart keep collecting into the order it just fired?
 * (F-FE-N112)
 *
 * ── The bug ──
 *
 * `submitCartMutation.onSuccess` re-loaded the order it had just created into
 * `continuingOrderId`, and `if (continuingOrderId)` is the SOLE condition that
 * routes the next submit to `addItemsToOrder`. So on the tablet a cashier fired
 * an order, went back to the menu believing they were starting a fresh sale, and
 * their taps were appended to the previous customer's ticket — firing a second
 * KOT against it. Leaving the screen and coming back "fixed" it, because that
 * unmounts the component and the state goes back to null.
 *
 * The offline sibling already refused to do this, and said why:
 *
 *   "A fresh capture does NOT stay loaded … the next customer's items would land
 *    on the previous customer's order."
 *
 * That reasoning was never applied to the online path.
 *
 * ── The rule ──
 *
 * A dine-in TABLE keeps collecting: a table legitimately runs one tab across
 * rounds, and the backend enforces exactly that — `OpenCounterTableTab` takes a
 * row lock and permits one open order per occupied table. Blanking there would
 * have the cashier start a second order on a table that already has one.
 *
 * Everything else returns to a blank slate: takeaway, delivery, and dine-in with
 * no table — which is how Mizaj actually fire (their kitchen tickets carry no
 * TABLE banner). Appending to one of those is still one tap, by selecting the
 * order's own card: that path runs `applyActiveOrderToCounterState`, which arms
 * the binding deliberately rather than by leftover.
 *
 * ── Why the tablet only ──
 *
 * On a desktop terminal the ticket rail is always on screen: the order number,
 * the "Fired" pill and the × are right there, and firing then taking payment on
 * the same ticket is the normal counter rhythm. The rail is what makes the
 * loaded order legible, and the tablet hides it while the cashier is on the menu
 * tab. So the surprise is a property of the tablet, and so is the fix — the
 * desktop counter keeps today's behaviour exactly.
 */

export interface FiredOrderBindingInput {
  /** Did this submit create an order, or add to one already loaded? */
  mode: 'new' | 'continue'
  /** Is the fired order a dine-in table tab? */
  hasTable: boolean
  /** Is this the Bhookly Server tablet acting as a till? */
  isTabletTill: boolean
}

/**
 * True when the fired order should stay armed as the cart's append target.
 *
 * Written so that `isTabletTill === false` is ALWAYS true — the desktop path is
 * unchanged by construction, not by a branch someone has to remember.
 */
export function shouldKeepCollecting({
  mode,
  hasTable,
  isTabletTill,
}: FiredOrderBindingInput): boolean {
  if (!isTabletTill) return true
  // Already appending: the cashier chose this ticket, so leaving it armed is
  // what they asked for. Disarming here would make every round after the first
  // need the card selected again.
  if (mode === 'continue') return true
  return hasTable
}
